/**
 * =============================================================================
 * RFI ADMIN LABELS
 * =============================================================================
 *
 * Admin-UI-local label + badge-variant maps for the staff RFI panel (PR G,
 * Decision D-4). These are the ADMIN 5-state review vocabulary and 5-state RFI
 * status vocabulary — deliberately SEPARATE from `PUBLIC_RFI_STATE_LABELS`
 * (the public 3-state map, which must NOT be reused here).
 *
 * =============================================================================
 */

import type { RfiItemReviewStatus, RfiItemType, RfiStatus } from '@find-my-part/types';

/** BigDesign `Badge` variant set used by the RFI badges. */
export type BadgeVariant = 'danger' | 'secondary' | 'success' | 'warning' | 'primary';

/** Admin label for each per-item review status. `OPEN` is "no responses yet". */
export const RFI_ITEM_REVIEW_STATUS_LABELS: Record<RfiItemReviewStatus, string> = {
  OPEN: 'No responses yet',
  UNREVIEWED: 'Awaiting review',
  INSUFFICIENT: 'Insufficient',
  PARTIAL: 'Partial',
  COMPLETE: 'Complete',
};

/** Badge variant for each per-item review status. */
export const RFI_ITEM_REVIEW_STATUS_VARIANTS: Record<RfiItemReviewStatus, BadgeVariant> = {
  OPEN: 'secondary',
  UNREVIEWED: 'warning',
  INSUFFICIENT: 'danger',
  PARTIAL: 'warning',
  COMPLETE: 'success',
};

/** Admin label for each RFI rollup status (incl. terminal SUPERSEDED/CANCELLED). */
export const RFI_STATUS_LABELS: Record<RfiStatus, string> = {
  OPEN: 'Open',
  PARTIALLY_ANSWERED: 'Partially answered',
  ANSWERED: 'Answered',
  SUPERSEDED: 'Superseded',
  CANCELLED: 'Cancelled',
};

/** Badge variant for each RFI rollup status. */
export const RFI_STATUS_VARIANTS: Record<RfiStatus, BadgeVariant> = {
  OPEN: 'secondary',
  PARTIALLY_ANSWERED: 'warning',
  ANSWERED: 'success',
  SUPERSEDED: 'secondary',
  CANCELLED: 'secondary',
};

/** Human label for each item type (used in the review display). */
export const RFI_ITEM_TYPE_LABELS: Record<RfiItemType, string> = {
  photo: 'Photo',
  measurement: 'Measurement',
  text: 'Text',
  selection: 'Selection',
  confirmation: 'Confirmation',
};

/**
 * RFI statuses that are read-only on the admin surface: every mutation against
 * a `SUPERSEDED`/`CANCELLED` RFI is rejected 409 by the worker, so the UI must
 * render them but offer no actions. (These are never produced today, but the
 * type includes them — the UI must not crash on them.)
 */
export function isTerminalRfiStatus(status: RfiStatus): boolean {
  return status === 'SUPERSEDED' || status === 'CANCELLED';
}

/** Default length of a reply body excerpt shown in the picker / linked badges. */
export const RFI_BODY_EXCERPT_LENGTH = 120;

/**
 * Build a single-line excerpt of a reply body (the candidate reply's id is an
 * internal identifier, not primary display — the body excerpt identifies it).
 * Collapses whitespace and appends an ellipsis when truncated.
 */
export function buildReplyExcerpt(body: string, max: number = RFI_BODY_EXCERPT_LENGTH): string {
  const normalized = body.replace(/\s+/g, ' ').trim();
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max).trimEnd()}…`;
}
